//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import { IStreamsDrawerState } from '@streams-drawer/model/AppState';
import { StreamsDrawerActionNames } from '@streams-drawer/model/StreamsDrawerActionNames';
import '@streams-drawer/model/StreamsDrawerActionNames';
import { StreamsDrawerContentViews } from '@streams-drawer/streams-drawer-constants';
import { PojoSet } from 'pojo-sets';

const initialState: IStreamsDrawerState = {
  brandInstagramId: undefined,
  brandInstagramUsername: undefined,
  brandFacebookPageName: undefined,
  clientRejectionReasons: undefined,
  contentView: StreamsDrawerContentViews.GRID,
  filters: undefined,
  lastSavedFilters: undefined,
  isAssetFeedsOnboarding: false,
  isContentDiscoveryEnabled: false,
  openStreamModal: undefined,
  isDeleteStreamModalOpen: false,
  isAutoApprovalEntryModalOpen: false,
  isAutoApprovalModalOpen: false,
  isStreamsDrawerOpen: false,
  isEndOfContentReached: false,
  isFiltersSidebarOpen: false,
  isFetchingBrandInstagramUsername: false,
  isFetchingBrandFacebookPageName: false,
  isFetchingFirstRunStatus: false,
  isFetchingFilteredItems: false,
  isFetchingItems: false,
  isFetchingNextItems: false,
  isRefreshingStreams: false,
  isRejectionReasonsModalOpen: false,
  isRemovalConfirmationModalOpen: false,
  items: [],
  rejectionReasonModalCuid: undefined,
  requestContentNonce: undefined,
  selectedStream: null,
  selectedStreamType: undefined,
  showContentDiscoveryFirstRun: false,
  sort: undefined,
  moderationStatus: undefined,
  sources: [],
  streams: [],
  summaryItemSets: [],
  recommendationsStreams: [],
  totalItemCount: 0,
  taggedByCuralatePendingPreviewItems: [],
  taggedByCuralatePendingCount: 0,
  taggedByBvAutomationConfiguration: [],
  taggedByBvAutomationActiveConfiguration: undefined,
  connectPendingCount: 0,
  trackingTimeout: undefined,
  connectVendor: undefined,
  creatorClients: [],
  mentionsMyBrand: [],
  gallerySelection: {},
};

export default function StreamsDrawerReducer(
  state: IStreamsDrawerState = initialState,
  action,
) {
  switch (action.type) {
    case StreamsDrawerActionNames.CLOSE_FILTERS_SIDEBAR:
      return {
        ...state,
        isFiltersSidebarOpen: false,
      };
    case StreamsDrawerActionNames.CLOSE_CONTENT_DISCOVERY_FIRST_RUN:
      return {
        ...state,
        showContentDiscoveryFirstRun: false,
      };
    case StreamsDrawerActionNames.CLOSE_STREAM_MODAL:
      return {
        ...state,
        openStreamModal: null,
      };
    case StreamsDrawerActionNames.CLOSE_STREAMS_DRAWER:
      return {
        ...state,
        isStreamsDrawerOpen: false,
      };
    case StreamsDrawerActionNames.CLOSE_REMOVAL_CONFIRMATION_MODAL:
      return {
        ...state,
        isRemovalConfirmationModalOpen: false,
      };
    case StreamsDrawerActionNames.FINISH_REFRESHING_STREAMS:
      return {
        ...state,
        isRefreshingStreams: false,
      };
    case StreamsDrawerActionNames.OPEN_FILTERS_SIDEBAR:
      return {
        ...state,
        isFiltersSidebarOpen: true,
      };
    case StreamsDrawerActionNames.OPEN_REMOVAL_CONFIRMATION_MODAL:
      return {
        ...state,
        isRemovalConfirmationModalOpen: true,
      };
    case StreamsDrawerActionNames.OPEN_STREAM_MODAL:
      return {
        ...state,
        openStreamModal: action.modalType,
      };
    case StreamsDrawerActionNames.OPEN_DELETE_STREAM_CONFIRMATION_MODAL:
      return {
        ...state,
        isDeleteStreamModalOpen: true,
      };

    case StreamsDrawerActionNames.OPEN_AUTO_APPROVAL_ENTRY_MODAL:
      return {
        ...state,
        isAutoApprovalEntryModalOpen: true,
      };

    case StreamsDrawerActionNames.CLOSE_AUTO_APPROVAL_ENTRY_MODAL:
      return {
        ...state,
        isAutoApprovalEntryModalOpen: false,
      };

    case StreamsDrawerActionNames.OPEN_AUTO_APPROVAL_MODAL:
      return {
        ...state,
        isAutoApprovalModalOpen: true,
      };

    case StreamsDrawerActionNames.CLOSE_AUTO_APPROVAL_MODAL:
      return {
        ...state,
        isAutoApprovalModalOpen: false,
      };

    case StreamsDrawerActionNames.CLOSE_DELETE_STREAM_CONFIRMATION_MODAL:
      return {
        ...state,
        isDeleteStreamModalOpen: false,
      };

    case StreamsDrawerActionNames.OPEN_STREAMS_DRAWER:
      return {
        ...state,
        isStreamsDrawerOpen: true,
      };
    case StreamsDrawerActionNames.OPEN_REJECTION_REASONS_MODAL:
      return {
        ...state,
        isRejectionReasonsModalOpen: true,
        rejectionReasonModalCuid: action.cuid,
      };
    case StreamsDrawerActionNames.CLOSE_REJECTION_REASONS_MODAL:
      return {
        ...state,
        isRejectionReasonsModalOpen: false,
        rejectionReasonModalCuid: undefined,
      };
    case StreamsDrawerActionNames.REQUEST_CLIENT_REJECTION_REASONS:
      return {
        ...state,
      };
    case StreamsDrawerActionNames.RECEIVE_CLIENT_REJECTION_REASONS_FAILED:
      return {
        ...state,
        clientRejectionReasons: undefined,
      };
    case StreamsDrawerActionNames.RECEIVE_CLIENT_REJECTION_REASONS_SUCCEEDED:
      return {
        ...state,
        clientRejectionReasons: action.clientRejectionReasons,
      };
    case StreamsDrawerActionNames.RECEIVE_BRAND_INSTAGRAM_FAILED:
      return {
        ...state,
        brandInstagramUsername: undefined,
        isFetchingBrandInstagramUsername: false,
      };
    case StreamsDrawerActionNames.RECEIVE_BRAND_INSTAGRAM_SUCCEEDED:
      return {
        ...state,
        brandInstagramId: action.brandInstagramId,
        brandInstagramUsername: action.brandInstagramUsername,
        isFetchingBrandInstagramUsername: false,
      };
    case StreamsDrawerActionNames.RECEIVE_BRAND_FACEBOOK_FAILED:
      return {
        ...state,
        brandFacebookPageName: undefined,
        isFetchingBrandFacebookPageName: false,
      };
    case StreamsDrawerActionNames.RECEIVE_BRAND_FACEBOOK_SUCCEEDED:
      return {
        ...state,
        brandFacebookPageName: action.pageName,
        isFetchingBrandFacebookPageName: false,
      };
    case StreamsDrawerActionNames.RECEIVE_CONTENT_ITEMS_SUCCEEDED:
      return {
        ...state,
        isFetchingItems: false,
        isFetchingFilteredItems: false,
        items: action.items,
        totalItemCount: action.totalItemCount,
      };
    case StreamsDrawerActionNames.UPDATE_ITEMS_IN_PLACE:
      return {
        ...state,
        items: action.items,
        totalItemCount: action.totalItemCount,
      };
    case StreamsDrawerActionNames.RECEIVE_FIRST_RUN_STATUS_SUCCEEDED:
      return {
        ...state,
        isFetchingFirstRunStatus: false,
        showContentDiscoveryFirstRun: action.showFirstRun,
      };
    case StreamsDrawerActionNames.RECEIVE_FILTERED_CONTENT_ITEMS_SUCCEEDED:
      return {
        ...state,
        isFetchingFilteredItems: false,
        items: action.items,
        totalItemCount: action.totalItemCount,
      };
    case StreamsDrawerActionNames.RECEIVE_NEXT_CONTENT_ITEMS_SUCCEEDED:
      return {
        ...state,
        isFetchingNextItems: false,
        items: action.items,
      };
    case StreamsDrawerActionNames.RECEIVE_SUMMARY_ITEMS_SUCCEEDED:
      return {
        ...state,
        isFetchingItems: false,
        summaryItemSets: action.itemSets,
      };
    case StreamsDrawerActionNames.SET_TAGGED_BY_CURALATE_PENDING_COUNT:
      return {
        ...state,
        taggedByCuralatePendingCount: action.taggedByCuralatePendingCount,
        taggedByCuralatePendingPreviewItems:
          action.taggedByCuralatePendingPreviewItems,
      };
    case StreamsDrawerActionNames.SET_CONNECT_PENDING_COUNT:
      return {
        ...state,
        connectPendingCount: action.connectPendingCount,
      };
    case StreamsDrawerActionNames.REMOVE_ITEMS:
      const currentItems = [...state.items];
      const updatedItems = currentItems.filter(
        (item) => !action.cuids.includes(`${item.itemType}.${item.id}`),
      );

      return {
        ...state,
        items: updatedItems,
      };
    case StreamsDrawerActionNames.REQUEST_BRAND_INSTAGRAM:
      return {
        ...state,
        isFetchingBrandInstagramUsername: true,
      };
    case StreamsDrawerActionNames.REQUEST_BRAND_FACEBOOK:
      return {
        ...state,
        isFetchingBrandFacebookPageName: true,
      };
    case StreamsDrawerActionNames.REQUEST_CONTENT_ITEMS:
      return {
        ...state,
        isFetchingItems: true,
      };
    case StreamsDrawerActionNames.REQUEST_FIRST_RUN_STATUS:
      return {
        ...state,
        isFetchingFirstRunStatus: true,
      };
    case StreamsDrawerActionNames.REQUEST_SUMMARY_ITEMS:
      return {
        ...state,
        isFetchingItems: true,
      };
    case StreamsDrawerActionNames.REQUEST_FILTERED_CONTENT_ITEMS:
      return {
        ...state,
        isFetchingFilteredItems: true,
        isFetchingItems: false,
      };
    case StreamsDrawerActionNames.REQUEST_NEXT_CONTENT_ITEMS:
      return {
        ...state,
        isFetchingNextItems: true,
      };
    case StreamsDrawerActionNames.SELECT_STREAM:
      return {
        ...state,
        isEndOfContentReached: false,
        isFetchingFilteredItems: false,
        isFetchingItems: false,
        isFetchingNextItems: false,
        selectedStream: action.stream,
      };
    case StreamsDrawerActionNames.SET_CONTENT_DISCOVERY_FLAG:
      return {
        ...state,
        isContentDiscoveryEnabled: action.isContentDiscoveryEnabled,
      };
    case StreamsDrawerActionNames.SET_CONTENT_VIEW:
      return {
        ...state,
        contentView: action.contentView,
      };
    case StreamsDrawerActionNames.SET_END_OF_CONTENT_REACHED:
      return {
        ...state,
        isEndOfContentReached: action.isEndOfContentReached,
      };
    case StreamsDrawerActionNames.SET_FILTERS:
      return {
        ...state,
        filters: action.filters,
      };
    case StreamsDrawerActionNames.SET_LAST_SAVED_FILTERS:
      return {
        ...state,
        lastSavedFilters: action.filters,
      };
    case StreamsDrawerActionNames.SET_REQUEST_CONTENT_NONCE:
      return {
        ...state,
        requestContentNonce: action.nonce,
      };
    case StreamsDrawerActionNames.SET_SELECTED_STREAM_TYPE:
      return {
        ...state,
        selectedStreamType: action.selectedStreamType,
      };
    case StreamsDrawerActionNames.SET_SOURCES:
      return {
        ...state,
        sources: action.sources,
      };
    case StreamsDrawerActionNames.SET_STREAMS:
      return {
        ...state,
        streams: action.streams,
      };
    case StreamsDrawerActionNames.SET_CURRENT_STREAM_NAME:
      const selectedStream = state.selectedStream;

      const updatedStream = {
        ...selectedStream,
        name: action.name,
      };

      const updatedStreams = state.streams.map((stream) => {
        if (stream.id === selectedStream.id) {
          return updatedStream;
        } else {
          return stream;
        }
      });

      return {
        ...state,
        streams: updatedStreams,
        selectedStream: updatedStream,
      };
    case StreamsDrawerActionNames.SET_SORT:
      return {
        ...state,
        sort: action.sort,
      };
    case StreamsDrawerActionNames.SET_MENTIONS_MY_BRAND:
      return {
        ...state,
        mentionsMyBrand: action.mentions,
      };
    case StreamsDrawerActionNames.SET_MODERATION_STATUS:
      return {
        ...state,
        moderationStatus: action.status,
      };
    case StreamsDrawerActionNames.SET_CONNECT_VENDOR:
      return {
        ...state,
        connectVendor: action.vendor,
      };
    case StreamsDrawerActionNames.SET_CREATOR_CLIENTS:
      return {
        ...state,
        creatorClients: action.clients,
      };
    case StreamsDrawerActionNames.SET_RECOMMENDATIONS_STREAMS:
      return {
        ...state,
        recommendationsStreams: action.recommendationsStreams,
      };
    case StreamsDrawerActionNames.SET_TRACKING_SOURCE_TIMEOUT:
      return {
        ...state,
        trackingTimeout: action.trackingTimeout,
      };
    case StreamsDrawerActionNames.START_REFRESHING_STREAMS:
      return {
        ...state,
        isRefreshingStreams: true,
      };
    case StreamsDrawerActionNames.SET_ASSET_FEEDS_ONBOARDING:
      return {
        ...state,
        isAssetFeedsOnboarding: action.isAssetFeedsOnboarding,
      };
    case StreamsDrawerActionNames.RESET_ASSET_FEEDS_DRAWER:
      return {
        ...state,
        items: [],
        selectedStream: null,
        streams: [],
      };
    case StreamsDrawerActionNames.RECEIVE_CLIENT_TBB_AUTOMATION_CONFIGURATION_FAILED:
      return {
        ...state,
      };
    case StreamsDrawerActionNames.RECEIVE_CLIENT_TBB_AUTOMATION_CONFIGURATION_SUCCEEDED:
      return {
        ...state,
        taggedByBvAutomationConfiguration:
          action.taggedByBvAutomationConfiguration,
      };
    case StreamsDrawerActionNames.RECEIVE_CLIENT_TBB_ACTIVE_AUTOMATION_CONFIGURATION_SUCCEEDED:
      return {
        ...state,
        taggedByBvAutomationActiveConfiguration:
          action.taggedByBvAutomationActiveConfiguration,
      };
    case StreamsDrawerActionNames.RECEIVE_CLIENT_TBB_ACTIVE_NO_AUTOMATION_CONFIGURATION:
      return {
        ...state,
        taggedByBvAutomationActiveConfiguration: undefined,
      };
    case StreamsDrawerActionNames.ADD_MODAL_SELECT_GALLERY:
      return {
        ...state,
        gallerySelection: PojoSet.add(state.gallerySelection, action.reelId),
      };
    case StreamsDrawerActionNames.ADD_MODAL_DESELECT_GALLERY:
      return {
        ...state,
        gallerySelection: PojoSet.remove(state.gallerySelection, action.reelId),
      };
    default:
      return state;
  }
}
